/**
 * CartItems Class
 * Direct conversion from TypeScript master cart/cartItems.ts
 */
// @ts-nocheck
import CartManager from '@theme/cart-manager';
import { formatMoney, imgURL } from '@theme/shopify-utils';
import {
  countLimitedSwatchUnits,
  isLimitedSwatchProductType,
  notifyDuplicateSwatch,
  notifySwatchLimit,
  SWATCH_LIMIT
} from '@theme/swatch-limit';

/** Build stamp — check window.__CART_ITEMS_BUILD__ in DevTools to confirm this file loaded */
export const CART_ITEMS_BUILD = 'mobile-cart-3b-v4';
if (typeof window !== 'undefined') {
  window.__CART_ITEMS_BUILD__ = CART_ITEMS_BUILD;
}

class CartItems {
  constructor(items = [], parent = document.body) {
    this.mediaDimension = '96x96';
    this.cartManager = CartManager.getInstance();
    // Do not blanket-hide "Note" — only Final Sale Notes are filtered below
    this.disallowedProps = ['Media', 'Certificate', 'Final Sale'];
    this.items = items;
    this.parent = parent;
    this.noProductTemplateElement = document.getElementById('empty-cart-template');
    this.update(items);
  }

  /** Normalize property/option labels for reliable Final Sale matching */
  normalizeLabel(value) {
    return String(value ?? '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  isFinalSaleLabel(value) {
    return this.normalizeLabel(value) === 'final sale';
  }

  /** True when this line property should stay off the drawer list (still on checkout/order) */
  isHiddenFinalSaleProperty(key, value) {
    const k = this.normalizeLabel(key);
    const v = this.normalizeLabel(value);

    // Legacy key: Final Sale: Final Sale
    if (k === 'final sale') return true;

    // New key: Note: Final Sale (or Note starting with Final Sale)
    if (k === 'note' && (v === 'final sale' || v.startsWith('final sale'))) return true;

    const line = `${k}: ${v}`;
    return line === 'final sale: final sale' || line === 'note: final sale';
  }

  hasFinalSaleProperty(properties) {
    if (!properties) return false;
    return Object.entries(properties).some(([key, value]) => this.isHiddenFinalSaleProperty(key, value));
  }

  update(items) {
    this.items = items;
    try {
      const filteredItems = items.filter(item => !item.hidden);
      const itemsWithoutCloverly = filteredItems.filter(item => item.product_id != 7704109711498);

      if (itemsWithoutCloverly.length > 0) {
        this.renderItems(itemsWithoutCloverly);
      } else {
        this.renderEmptyCart();
      }
    } catch (err) {
      console.error('[CartItems.update] render failed', err, { items });
    }
  }

  renderItems(items) {
    // Clear existing content
    this.parent.innerHTML = '';

    // Render each item
    items.forEach(item => {
      const itemElement = this.createItemElement(item);
      this.parent.appendChild(itemElement);
    });
  }

  renderEmptyCart() {
    if (this.noProductTemplateElement) {
      this.parent.innerHTML = '';
      const emptyContent = this.noProductTemplateElement.content.cloneNode(true);
      this.parent.appendChild(emptyContent);
    }
  }

  createItemElement(item) {
    const div = document.createElement('div');

    // Check if item should be hidden
    const isProductHidden = item.metafields?.seo?.hidden === 1;
    const hideQty = !!item.properties?.['_rugBundleId'];

    // Build classes
    let classes = 'slideoutItemListing';
    if (isProductHidden) classes += ' slideoutItemListing--hidden';
    if (hideQty) classes += ' slideoutItemListing--hide-qty';

    div.className = classes;
    div.dataset.itemKey = item.key; // Store key for reference

    // Create content wrapper
    const content = document.createElement('div');
    content.className = 'slideoutItemListing__content';

    // Image
    const imageEl = this.createImageElement(item);
    if (imageEl) content.appendChild(imageEl);

    // Details
    const details = this.createDetailsElement(item);
    content.appendChild(details);

    // Actions
    const actions = this.createActionsElement(item);
    content.appendChild(actions);

    div.appendChild(content);

    const delivery = this.createDeliveryElement(item);
    if (delivery) div.appendChild(delivery);

    return div;
  }

  metafieldValue(value) {
    if (value && typeof value === 'object' && 'value' in value) return value.value;
    return value;
  }

  getLeadTime(item) {
    const directValue = this.metafieldValue(item.fulfillment?.lead_time);
    return String(directValue || '').trim();
  }

  isFurnitureInStock(item) {
    const mvnInventoryQuantity = Number(item.fulfillment?.mvn_inventory_quantity || 0);
    // Positive MVN inventory takes precedence over "Sell when out of stock".
    return mvnInventoryQuantity > 0;
  }

  getCategoryGroup(item) {
    const categoryNames = Array.isArray(item.fulfillment?.product_category_names)
      ? item.fulfillment.product_category_names
      : [];
    const usableCategoryNames = categoryNames
      .map(value => String(value || '').trim())
      .filter(value => value && value.toLowerCase() !== 'uncategorized');
    const classificationValues = usableCategoryNames.length > 0
      ? usableCategoryNames
      : [item.product_type || ''];
    const classification = classificationValues
      .map(value => String(value || '').toLowerCase().trim())
      .filter(Boolean)
      .join(' | ');

    if (/\brug(s)?\b/.test(classification) || /\blighting\b/.test(classification)) {
      return 'rugs-lighting';
    }

    if (/\bfurniture\b/.test(classification)) return 'furniture';
    return 'other';
  }

  cleanDeliveryText(value) {
    return String(value || '')
      .replace(/^lead\s*time\s*:\s*/i, '')
      .replace(/^(in\s*stock|made\s*to\s*order|mto)\s*[-—:]?\s*/i, '')
      .replace(/[.!]\s*$/, '')
      .trim();
  }

  leadTimePhrase(leadTime) {
    const cleanValue = this.cleanDeliveryText(leadTime)
      .replace(/^(arrives?|ships?)\s+(in\s+)?/i, '')
      .trim();
    return cleanValue ? `arrives in ${cleanValue}` : '';
  }

  leadTimeSegment(leadTime, useLocalStoreSegment) {
    const commaIndex = leadTime.indexOf(',');
    if (commaIndex === -1) {
      return this.cleanDeliveryText(leadTime);
    }

    const segment = useLocalStoreSegment
      ? leadTime.slice(0, commaIndex)
      : leadTime.slice(commaIndex + 1);
    return this.cleanDeliveryText(segment);
  }

  appendDeliveryContent(block, labelText, deliveryText = '') {
    if (labelText) {
      const label = document.createElement('strong');
      label.textContent = deliveryText ? `${labelText} - ` : labelText;
      block.appendChild(label);
    }

    if (!deliveryText) return block;

    block.appendChild(document.createTextNode(deliveryText));
    if (!/[.!?]$/.test(deliveryText)) {
      block.appendChild(document.createTextNode('.'));
    }
    return block;
  }

  createDeliveryElement(item) {
    const leadTime = this.getLeadTime(item);
    const categoryGroup = this.getCategoryGroup(item);
    const block = document.createElement('p');
    block.className = 'slideoutItemListing__delivery';

    if (categoryGroup === 'furniture') {
      if (this.isFurnitureInStock(item)) {
        return this.appendDeliveryContent(block, 'In Stock', 'arrives in 1-3 weeks');
      }

      block.classList.add('slideoutItemListing__delivery--madeToOrder');
      return this.appendDeliveryContent(block, 'Made to Order', this.leadTimePhrase(leadTime));
    }

    if (categoryGroup === 'rugs-lighting') {
      return this.appendDeliveryContent(block, 'In Stock', this.leadTimePhrase(leadTime));
    }

    const hasLocalStoreInventory = item.fulfillment?.local_store_inventory_available === true;
    const selectedLeadTime = this.leadTimeSegment(leadTime, hasLocalStoreInventory);
    if (!hasLocalStoreInventory) {
      block.classList.add('slideoutItemListing__delivery--madeToOrder');
    }

    // A single-value lead time has no local/non-local segments, so display it
    // with an "Arrives in" prefix instead of adding an inventory-status label.
    if (leadTime && !leadTime.includes(',')) {
      const standaloneLeadTime = this.leadTimePhrase(selectedLeadTime)
        .replace(/^arrives\b/, 'Arrives');
      return this.appendDeliveryContent(block, '', standaloneLeadTime);
    }

    return this.appendDeliveryContent(
      block,
      hasLocalStoreInventory ? 'In Stock' : 'Made to Order',
      selectedLeadTime
    );
  }

  createImageElement(item) {
    const mirrorImage = item.properties?.['_mirrorImage'] || '';
    const mediaGalleryImage = item.media_gallery_image || '';
    const hasImg = typeof item.image === 'string' && item.image.length > 0;
    const hasMirrorImg = typeof mirrorImage === 'string' && mirrorImage.length > 0;
    const hasMedia = typeof mediaGalleryImage === 'string' && mediaGalleryImage.length > 0;
    const isNoImage = hasImg && item.image.indexOf('no-image') !== -1;
    const isImageEmpty = (!hasImg && !hasMedia && !hasMirrorImg) || isNoImage;

    if (isImageEmpty) {
      const emptyDiv = document.createElement('div');
      emptyDiv.className = 'slideoutItemListing__image slideoutItemListing__image--empty';
      emptyDiv.setAttribute('aria-hidden', 'true');
      return emptyDiv;
    }

    const src = hasMirrorImg ? mirrorImage : (hasMedia ? mediaGalleryImage : (hasImg ? item.image : null));
    if (!src) return null;

    // Convert image URL to 96x96 size
    const imgUrl = this.getImageUrl(src, this.mediaDimension);

    const imageContainer = document.createElement('div');
    imageContainer.className = 'slideoutItemListing__image';

    const isProductHidden = item.metafields?.seo?.hidden === 1;
    const href = isProductHidden ? undefined : item.url;

    const img = document.createElement('img');
    img.src = imgUrl;
    img.alt = item.title || '';
    img.className = 'slideoutItemListing__img';

    if (href) {
      const link = document.createElement('a');
      link.href = href;
      link.appendChild(img);
      imageContainer.appendChild(link);
    } else {
      imageContainer.appendChild(img);
    }

    return imageContainer;
  }

  createDetailsElement(item) {
    const details = document.createElement('div');
    details.className = 'slideoutItemListing__details';

    // Badges
    if (item.badges && item.badges.length > 0) {
      const badgesDiv = document.createElement('div');
      badgesDiv.className = 'product-badges slideoutItemListing__badges';
      item.badges.forEach(badge => {
        const badgeSpan = document.createElement('span');
        badgeSpan.className = 'product-badge product-badge--promo slideoutItemListing__badge';
        badgeSpan.style.setProperty('--color-text', badge.color || '');
        badgeSpan.style.setProperty('--color-bg', badge.background || '');
        badgeSpan.textContent = badge.text || '';
        badgesDiv.appendChild(badgeSpan);
      });
      details.appendChild(badgesDiv);
    }

    // Title
    const title = document.createElement('h4');
    title.className = 'slideoutItemListing__name';
    const isProductHidden = item.metafields?.seo?.hidden === 1;
    const itemTitle = item.metafields?.custom?.short_title || item.product_title || '';
    const href = isProductHidden ? undefined : item.url;

    if (href) {
      const link = document.createElement('a');
      link.href = href;
      link.textContent = itemTitle;
      title.appendChild(link);
    } else {
      title.textContent = itemTitle;
    }
    details.appendChild(title);

    // Price
    const priceEl = this.createPriceElement(item);
    details.appendChild(priceEl);

    if (item.free_shipping) {
      const infoSpan = document.createElement('span');
      infoSpan.style.display = 'block';
      infoSpan.style.fontSize = '12px';
      infoSpan.style.color = '#fd619c';
      infoSpan.style.marginTop = '4px';
      infoSpan.style.marginBottom = '10px';
      infoSpan.textContent = 'Free Shipping';
      details.appendChild(infoSpan);
    }

    const isFinalSaleItem = this.hasFinalSaleProperty(item.properties);
    if (isFinalSaleItem) {
      const finalSaleSpan = document.createElement('span');
      finalSaleSpan.style.color = 'var(--c-pink)';
      finalSaleSpan.textContent = 'Final Sale';
      details.appendChild(finalSaleSpan);
    }

    // Variant info (options + other properties; Final Sale is shown as a tag above)
    const hasDisplayableProps = item.properties &&
      Object.keys(item.properties).some((k) => {
        if (this.isHiddenFinalSaleProperty(k, item.properties[k])) return false;
        if (this.disallowedProps.some((p) => this.normalizeLabel(p) === this.normalizeLabel(k))) return false;
        if (String(k).startsWith('_')) return false;
        return String(k).trim() !== '';
      });

    if (!item.product_has_only_default_variant || hasDisplayableProps) {
      const variantInfo = this.createVariantInfoElement(item);
      if (variantInfo) {
        if (item.free_shipping) {
          const variantUl = variantInfo.querySelector('.slideoutItemListing__variantInfo');
          if (variantUl) variantUl.style.margin = '0';
        }
        details.appendChild(variantInfo);
      }
    }

    return details;
  }

  createPriceElement(item) {
    const priceSpan = document.createElement('span');
    priceSpan.className = 'price-value slideoutItemListing__value';

    // Calculate prices
    let customPrice = +item.properties?.['_rugBundlePrice'] || 0;
    const addOnPrice = +item.properties?.['_add_on_price'] || 0;
    const addOnMatchQuantity = item.properties?.['_add_on_match_qty'];
    let totalAddOnPrice = addOnPrice;

    if (addOnMatchQuantity === 'true') {
      totalAddOnPrice = addOnPrice * item.quantity;
    }

    if (addOnPrice) {
      customPrice = item.final_line_price - totalAddOnPrice;
    }

    // Check if customer is B2B
    const isB2B = window.ShopifyThemeSettings?.isB2BCustomer || false;
    const hasVariant = !!item.variant;
    
    // Get B2B prices if customer is B2B
    // Note: variant metafields are stored in item.metafields.variant (from parseCustomData)
    let b2bUnitPrice = null;
    let b2bComparePrice = null;
    
    if (isB2B && item.metafields?.variant) {
      // Try to get B2B price from variant metafields
      // Metafields are stored in dollars, formatMoney expects cents
      const b2bPriceNum = parseFloat(item.metafields.variant.b2b_price);
      if (Number.isFinite(b2bPriceNum)) {
        b2bUnitPrice = Math.round(b2bPriceNum * 100);
      }

      const b2bCompareNum = parseFloat(item.metafields.variant.b2b_compare_at_price);
      if (Number.isFinite(b2bCompareNum)) {
        b2bComparePrice = Math.round(b2bCompareNum * 100);
      }
    }
    
    // Calculate line price - use B2B price if available
    let linePrice;
    if (isB2B && b2bUnitPrice != null) {
      linePrice = (b2bUnitPrice * item.quantity) - totalAddOnPrice;
    } else {
      linePrice = customPrice || item.final_line_price || 0;
    }
    
    // Calculate compare price
    let compareAt;
    let vPrice;
    if (isB2B && b2bComparePrice != null) {
      compareAt = b2bComparePrice;
      vPrice = b2bUnitPrice || (hasVariant ? (item.variant.price ?? 0) : 0);
    } else {
      compareAt = hasVariant ? (item.variant.compare_at_price ?? 0) : 0;
      vPrice = hasVariant ? (item.variant.price ?? 0) : 0;
    }
    
    const showCompare = (item.original_price !== item.final_price) || (compareAt > vPrice);

    if (showCompare) {
      const comparePrice = compareAt > vPrice
        ? (compareAt * item.quantity) - totalAddOnPrice
        : (item.original_line_price - totalAddOnPrice);

      const compareEl = document.createElement('s');
      compareEl.className = 'price-value__compare';
      compareEl.title = 'Regular Price';
      compareEl.textContent = this.formatMoneyValue(comparePrice);
      priceSpan.appendChild(compareEl);
    }

    const currentEl = document.createElement('span');
    currentEl.className = 'price-value__current';
    currentEl.textContent = this.formatMoneyValue(linePrice);
    
    // Add pink color for B2B prices
    if (isB2B && b2bUnitPrice != null) {
      currentEl.style.color = '#FD619C';
    }
    
    priceSpan.appendChild(currentEl);

    return priceSpan;
  }

  createVariantInfoElement(item) {
    const description = document.createElement('div');
    description.className = 'slideoutItemListing__description';

    const ul = document.createElement('ul');
    ul.className = 'slideoutItemListing__variantInfo';

    // Options (Color, Size, etc.) — skip Shopify's default "Title: Default Title"
    if (item.options_with_values && item.options_with_values.length > 0) {
      item.options_with_values.forEach(option => {
        if (option.name === 'Title' || option.value === 'Default Title') return;
        // Never list Final Sale as an option row in the drawer
        if (this.isFinalSaleLabel(option.name) || this.isHiddenFinalSaleProperty(option.name, option.value)) {
          return;
        }
        const li = document.createElement('li');
        li.className = 'slideoutItemListing__item';
        li.textContent = `${option.name}: ${option.value}`;
        ul.appendChild(li);
      });
    }

    // Properties (custom properties like Dimension, Shape, etc.)
    if (item.properties) {
      const shapeVal = (item.properties['Shape'] || '').toLowerCase().trim();
      const isRound = shapeVal === 'round';

      Object.entries(item.properties).forEach(([key, value]) => {
        const keyStr = String(key ?? '');
        const valueStr = String(value ?? '').trim();
        const keyTrimmed = keyStr.trim();

        // Hide Final Sale line props (legacy key or Note: Final Sale) — pink span instead
        if (key === 'Final Sale' || this.isHiddenFinalSaleProperty(keyStr, valueStr)) return;

        // Always skip underscore-prefixed and disallowed keys
        if (keyStr.startsWith('_')) return;
        if (this.disallowedProps.some((p) => this.normalizeLabel(p) === this.normalizeLabel(keyStr))) return;

        // Skip Shopify's default variant title
        if (keyTrimmed === 'Title' || valueStr === 'Default Title') return;

        // Skip blank keys (nothing to label)
        if (!keyTrimmed) return;

        // For round mirrors, skip Height (only diameter matters)
        if (isRound && keyTrimmed === 'Height') return;

        // Rename Width → Diameter for round mirrors
        let displayKey = keyTrimmed;
        if (isRound && keyTrimmed === 'Width') displayKey = 'Diameter';

        // Ensure inch symbol on Width/Diameter/Height values
        let displayValue = valueStr;
        if ((keyTrimmed === 'Width' || keyTrimmed === 'Height') && !displayValue.includes('"')) {
          displayValue = displayValue + '"';
        }

        // Final safety: never render "Final Sale: Final Sale" as a list row
        if (this.isHiddenFinalSaleProperty(displayKey, displayValue)) return;

        const li = document.createElement('li');
        li.className = 'slideoutItemListing__item';
        li.textContent = `${displayKey}: ${displayValue}`;
        ul.appendChild(li);
      });
    }

    // Add-on product info
    const addOnPrice = +item.properties?.['_add_on_price'] || 0;
    const addOnMatchQuantity = item.properties?.['_add_on_match_qty'];
    const addOnProductTitle = item.properties?.['_add_on_title'];
    let totalAddOnPrice = addOnPrice;

    if (addOnMatchQuantity === 'true') {
      totalAddOnPrice = addOnPrice * item.quantity;
    }

    if (totalAddOnPrice && addOnProductTitle) {
      const li = document.createElement('li');
      li.className = 'slideoutItemListing__item';
      const titleSpan = document.createElement('span');
      titleSpan.textContent = `+ ${addOnProductTitle}`;
      const priceSpan = document.createElement('span');
      priceSpan.className = 'price-value__current';
      priceSpan.textContent = this.formatMoneyValue(totalAddOnPrice);
      li.appendChild(titleSpan);
      li.appendChild(priceSpan);
      ul.appendChild(li);
    }

    if (ul.children.length > 0) {
      // Last-resort cleanup if a Final Sale row was added from options/properties
      Array.from(ul.children).forEach((li) => {
        const text = String(li.textContent || '')
          .replace(/\u00a0/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
        if (text === 'final sale: final sale' || text === 'note: final sale' || text === 'final sale') {
          li.remove();
        }
      });
    }

    if (ul.children.length > 0) {
      description.appendChild(ul);
      description.dataset.cartItemsBuild = CART_ITEMS_BUILD;
      return description;
    }

    return null;
  }

  createActionsElement(item) {
    const actions = document.createElement('div');
    actions.className = 'slideoutItemListing__actions';

    // Quantity selector
    const quantityDiv = document.createElement('div');
    quantityDiv.className = 'slideoutItemListing__quantity';

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'slideoutItemListing__quantityButton slideoutItemListing__quantityButton--remove';
    removeBtn.setAttribute('aria-label', 'Decrease quantity');
    removeBtn.disabled = item.quantity === 1;
    removeBtn.addEventListener('click', () => this.removeItemQty(item));
    quantityDiv.appendChild(removeBtn);

    const quantityValue = document.createElement('span');
    quantityValue.className = 'slideoutItemListing__quantityValue';
    quantityValue.textContent = item.quantity || 1;
    quantityDiv.appendChild(quantityValue);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'slideoutItemListing__quantityButton slideoutItemListing__quantityButton--add';
    addBtn.setAttribute('aria-label', 'Increase quantity');
    addBtn.addEventListener('click', () => this.addItemQty(item));
    quantityDiv.appendChild(addBtn);

    actions.appendChild(quantityDiv);

    // Remove link
    const actionLinks = document.createElement('div');
    actionLinks.className = 'slideoutItemListing__actionLinks';

    const removeLink = document.createElement('button');
    removeLink.type = 'button';
    removeLink.className = 'slideoutItemListing__actionLink slideoutItemListing__actionLink--remove';
    removeLink.textContent = 'Remove';
    removeLink.addEventListener('click', () => this.removeItem(item));
    actionLinks.appendChild(removeLink);

    actions.appendChild(actionLinks);

    return actions;
  }

  addItemQty(item) {
    const { key, quantity } = item;
    this.updateQuantity(key, quantity + 1);
  }

  removeItemQty(item) {
    const { key, quantity } = item;
    this.updateQuantity(key, Math.max(0, quantity - 1));
  }

  updateQuantity(key, qty) {
    const item = this.items.find(function (i) {
      return i.key === key;
    });

    if (item && isLimitedSwatchProductType(item.product_type) && qty > item.quantity) {
      if (countLimitedSwatchUnits(this.items) >= SWATCH_LIMIT) {
        notifySwatchLimit();
      } else {
        notifyDuplicateSwatch();
      }
      return;
    }

    this.disableActions();
    const properties =
      item && item.properties && item.properties._mirrorShape ? item.properties : undefined;
    this.cartManager.change(key, qty, properties).then(() => {
      this.enableActions();
      // Dispatch cart update event
      const event = new CustomEvent('cart:update', {
        detail: {
          data: {
            cart: this.cartManager.getState()
          }
        }
      });
      document.dispatchEvent(event);
    }).catch(() => {
      this.enableActions();
    });
  }

  removeItem(item) {
    // Direct conversion from TypeScript line 221-232
    const { key, properties } = item;

    console.log('[CartItems.removeItem] Item:', item);
    console.log('[CartItems.removeItem] Key:', key);

    if (properties?.['_rugBundleId']) {
      const bundleId = properties['_rugBundleId'];
      const bundled = this.items
        .filter(i => i.properties?.['_rugBundleId'] === bundleId)
        .map((i) => {
          console.log('[CartItems.removeItem] Bundle item key:', i.key, 'id:', i.id);
          return { quantity: 0, id: i.key || i.id };
        });

      console.log('[CartItems.removeItem] Bundled items to remove:', bundled);

      this.cartManager.changeMany(bundled).then(() => {
        const event = new CustomEvent('cart:update', {
          detail: {
            data: {
              cart: this.cartManager.getState()
            }
          }
        });
        document.dispatchEvent(event);
      }).catch(err => {
        console.error('[CartItems.removeItem] Error:', err);
        this.enableActions();
      });
    } else {
      console.log('[CartItems.removeItem] Single item, calling updateQuantity with key:', key);
      this.updateQuantity(key, 0);
    }
  }

  enableActions() {
    // Direct conversion from TypeScript line 234-239
    const actions = document.querySelectorAll('.cartItem .sign');
    for (let i = 0; i < actions.length; i++) {
      actions[i].disabled = false;
    }
  }

  disableActions() {
    // Direct conversion from TypeScript line 241-245
    const actions = document.querySelectorAll('.cartItem .sign');
    for (let i = 0; i < actions.length; i++) {
      actions[i].disabled = true;
    }
  }
  getImageUrl(src, dimension) {
    return imgURL(src, dimension);
  }
  formatMoneyValue(cents) {
    return formatMoney(cents);
  }
}
export default CartItems;

